/**
 * PATH: apps/qa/src/pages/area-execution/area-execution-api.ts
 * PURPOSE: API helper functions for area execution (fetch, update, lock, NCR, hold points)
 */

import api from '@/lib/api'
import type { Area, ChecklistInstance, Evidence, ItemInstance } from '@/types/qa'
import type { BackendItemStatus, UIItemStatus, Template } from './area-execution-types'
import { toBackendStatus } from './area-execution-types'

export async function fetchAreaDetails(areaId: string): Promise<Area & {
  is_locked: boolean; floor_name?: string; inspection_id?: string; job_id?: string
  locked_by_name?: string; name?: string; lock_reason?: string
}> {
  const res = await api().get(`/qa/areas/${areaId}/execution/`)
  const data = res.data
  return { ...data, name: data.area_name, is_locked: data.status === 'locked', floor_name: data.floor_name, inspection_id: data.inspection_id }
}

export async function fetchAreaItems(areaId: string): Promise<ItemInstance[]> {
  const res = await api().get(`/qa/item-instances/`, { params: { area_id: areaId } })
  const d = res.data
  if (Array.isArray(d)) return d
  if (d && Array.isArray(d.results)) return d.results
  return []
}

export async function fetchAreaEvidence(areaId: string): Promise<Evidence[]> {
  const res = await api().get(`/qa/evidence/`, { params: { area_id: areaId } })
  const d = res.data
  if (Array.isArray(d)) return d
  if (d && Array.isArray(d.results)) return d.results
  return []
}

export async function fetchTemplates(): Promise<Template[]> {
  const res = await api().get(`/qa/templates/`, { params: { status: 'published' } })
  const d = res.data
  if (Array.isArray(d)) return d
  if (d && Array.isArray(d.results)) return d.results
  return []
}

export async function updateItemStatus(
  itemId: string,
  status: UIItemStatus | BackendItemStatus,
  overrideReason?: string,
): Promise<ItemInstance> {
  const res = await api().post<ItemInstance>(`/qa/item-instances/${itemId}/set_status/`, {
    status: toBackendStatus(status), override_reason: overrideReason,
  })
  return res.data
}

/**
 * How far the work in the area has got, a separate field from the lock state.
 *
 * A dedicated action, not a PATCH on the area: generic area update is only
 * org-scoped, while this endpoint carries the AREA_EDIT check and writes an
 * audit row.
 */
export async function updateAreaWorkStatus(areaId: string, workStatus: BackendItemStatus): Promise<Area> {
  const res = await api().post<Area>(`/qa/areas/${areaId}/set-work-status/`, { work_status: workStatus })
  return res.data
}

/**
 * Confirm (or withdraw) the owner's verification of a completion claim.
 *
 * A dedicated action, never a PATCH: the two verification columns are read-only
 * on the serializer and this endpoint stamps the acting user, so nobody can
 * hand-write a timestamp or attribute a signature to someone else. 403s for any
 * actor who is not a verifier, so callers gate the control on the payload's
 * `can_verify_completion` rather than calling this and handling the refusal.
 */
export async function verifyItemCompletion(itemId: string, verified: boolean): Promise<ItemInstance> {
  const res = await api().post<ItemInstance>(
    `/qa/item-instances/${itemId}/verify-completion/`, { verified },
  )
  return res.data
}

export async function verifyAreaCompletion(areaId: string, verified: boolean): Promise<Area> {
  const res = await api().post<Area>(`/qa/areas/${areaId}/verify-completion/`, { verified })
  return res.data
}

/**
 * An ItemInstance has no notes column and set_status ignores a `notes` key, so
 * a note only persists as Evidence. `areaId` is required: EvidenceCreateSerializer
 * rejects a row carrying no inspection, floor or area.
 */
export async function addItemNote(areaId: string, itemInstanceId: string, noteText: string): Promise<Evidence> {
  const res = await api().post<Evidence>(`/qa/evidence/`, {
    area: areaId, item_instance: itemInstanceId, type: 'note',
    note_text: noteText, folder: 'Field Notes',
  })
  return res.data
}

export async function updateItemNote(evidenceId: string, noteText: string): Promise<Evidence> {
  const res = await api().patch<Evidence>(`/qa/evidence/${evidenceId}/`, { note_text: noteText })
  return res.data
}

export async function deleteItemNote(evidenceId: string): Promise<void> {
  await api().delete(`/qa/evidence/${evidenceId}/`)
}

export async function updateItemReading(itemId: string, capturedValue: string, capturedUnit: string): Promise<ItemInstance> {
  const res = await api().post<ItemInstance>(`/qa/item-instances/${itemId}/set-reading/`, {
    captured_value: capturedValue,
    captured_unit: capturedUnit,
  })
  return res.data
}

export async function lockArea(areaId: string, reason?: string): Promise<Area> {
  const res = await api().post(`/qa/areas/${areaId}/lock/`, { reason })
  return res.data
}

export async function unlockArea(areaId: string, reason: string): Promise<Area> {
  const res = await api().post(`/qa/areas/${areaId}/unlock/`, { reason })
  return res.data
}

export async function applyTemplate(templateId: string, areaIds: string[]): Promise<ChecklistInstance[]> {
  const res = await api().post(`/qa/checklist-instances/apply_template/`, { template_id: templateId, area_ids: areaIds })
  return res.data
}

export async function requestHoldPoint(areaId: string, name: string, itemInstanceId?: string) {
  const res = await api().post(`/qa/hold-points/request/`, { area_id: areaId, name, item_instance_id: itemInstanceId })
  return res.data
}

export async function createNCR(data: {
  job_id: string; inspection_id: string; area_id: string; item_instance_id?: string
  title: string; description: string; severity: string
}) {
  const res = await api().post(`/qa/ncrs/`, data)
  return res.data
}

export async function addAreaItem(
  areaId: string,
  fields: { title?: string; product?: string; requirement_text?: string; acceptance_criteria?: string } = {},
  insertAfterNumber?: string,
): Promise<ItemInstance> {
  const res = await api().post<ItemInstance>(`/qa/areas/${areaId}/add-item/`, {
    ...fields,
    ...(insertAfterNumber ? { insert_after_number: insertAfterNumber } : {}),
  })
  return res.data
}

export async function updateTemplateItem(
  templateItemId: string,
  fields: { title?: string; product?: string; requirement_text?: string; acceptance_criteria?: string },
) {
  const res = await api().patch(`/qa/template-items/${templateItemId}/`, fields)
  return res.data
}

export async function deleteAreaItem(itemInstanceId: string) {
  const res = await api().delete(`/qa/item-instances/${itemInstanceId}/`)
  return res.data
}

export async function raiseItemChange(
  itemInstanceId: string,
  kind: 'edit' | 'delete',
  fields: { title?: string; product?: string; requirement_text?: string; acceptance_criteria?: string } = {},
) {
  const res = await api().post(`/qa/item-change-requests/`, {
    item_instance: itemInstanceId, kind, ...fields,
  })
  return res.data
}

export async function approveItemChange(changeId: string, note?: string) {
  const res = await api().post(`/qa/item-change-requests/${changeId}/approve/`, note ? { note } : {})
  return res.data
}

export async function rejectItemChange(changeId: string, note?: string) {
  const res = await api().post(`/qa/item-change-requests/${changeId}/reject/`, note ? { note } : {})
  return res.data
}

export async function attachItemPhoto(areaId: string, itemInstanceId: string, assetId: string, description?: string) {
  const res = await api().post(`/qa/evidence/`, {
    area: areaId, item_instance: itemInstanceId, type: 'photo', asset_id: assetId,
    description: description ?? '', folder: '',
  })
  return res.data
}
